import { useListEvidenceQueryControllerList } from "@/generated/evidence/evidence";
import type { EvidenceRecordResponseDto } from "@/generated/model/evidenceRecordResponseDto";
import type { ListEvidenceQueryControllerListSortBy } from "@/generated/model/listEvidenceQueryControllerListSortBy";
import type { ListEvidenceQueryControllerListSortDir } from "@/generated/model/listEvidenceQueryControllerListSortDir";
import type { EvidenceSortColumn, SortDir } from "@/lib/evidence-hub";

interface UseEvidenceHubDataArgs {
  nurseryId?: string;
  categoryId?: string;
  sortBy: EvidenceSortColumn;
  sortDir: SortDir;
  page: number;
  pageSize: number;
}

/**
 * Drives the Evidence Hub list server-side: forwards the category filter, sort
 * and pagination, and maps the 0-based page to the backend's `limit`/`offset`.
 */
export function useEvidenceHubData({
  nurseryId,
  categoryId,
  sortBy,
  sortDir,
  page,
  pageSize,
}: UseEvidenceHubDataArgs) {
  const listQuery = useListEvidenceQueryControllerList(
    nurseryId ?? "",
    {
      categoryId,
      sortBy: sortBy as ListEvidenceQueryControllerListSortBy,
      sortDir: sortDir as ListEvidenceQueryControllerListSortDir,
      limit: pageSize,
      offset: page * pageSize,
    },
    // refetchOnMount "always": the global QueryClient defaults (staleTime 5min,
    // refetchOnMount false) would otherwise replay a cached page after a record
    // is completed elsewhere — leaving the evidence log stale.
    //
    // Gated on categoryId too: the server returns an empty page without one, so
    // this skips a guaranteed-empty request on every load, sort and page change.
    {
      query: { enabled: !!nurseryId && !!categoryId, refetchOnMount: "always" },
    },
  );

  const items: EvidenceRecordResponseDto[] = listQuery.data?.data.data ?? [];
  const total: number = listQuery.data?.data.meta.total ?? 0;

  return { items, total, isLoading: listQuery.isLoading };
}
